import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Mail, Lock, ArrowLeft, Shield, Check, X } from "lucide-react";
import { useAdminAuth } from "@/context/Admin";
import { getPasswordStrength, getStrengthText, getStrengthColor } from "@/lib/password-utils";

export default function AdminForgotPassword() {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { resetAdminPassword } = useAdminAuth();
  const navigate = useNavigate();

  const passwordStrength = getPasswordStrength(password);

  const handleOtpChange = (index, value) => {
    // Handle paste: if value length > 1, it's likely a paste operation
    if (value.length > 1) {
      const digits = value.replace(/\D/g, ""); // Extract only digits

      if (digits.length === 0) return;

      const newOtp = [...otp];

      // If we have 6 digits, fill all fields
      if (digits.length >= 6) {
        for (let i = 0; i < 6; i++) {
          newOtp[i] = digits[i] || "";
        }
        setOtp(newOtp);
        // Focus the last input
        const lastInput = document.getElementById(`admin-reset-otp-5`);
        if (lastInput) lastInput.focus();
        return;
      }

      // If less than 6 digits, fill from current index
      for (let i = 0; i < digits.length && (index + i) < 6; i++) {
        newOtp[index + i] = digits[i];
      }
      setOtp(newOtp);

      // Focus the next empty input or the last one
      const nextIndex = Math.min(index + digits.length, 5);
      const nextInput = document.getElementById(`admin-reset-otp-${nextIndex}`);
      if (nextInput) nextInput.focus();
      return;
    }

    // Handle single digit input
    const newOtp = [...otp];
    newOtp[index] = value.replace(/\D/g, ""); // Only allow digits
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`admin-reset-otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`admin-reset-otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleOtpPaste = (e, startIndex = 0) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text");
    const digits = pastedData.replace(/\D/g, ""); // Extract only digits

    if (digits.length === 0) return;

    const newOtp = [...otp];

    // If we have 6 or more digits, always fill from the beginning (index 0)
    // This provides a better UX - pasting "123456" should fill all fields
    if (digits.length >= 6) {
      for (let i = 0; i < 6; i++) {
        newOtp[i] = digits[i];
      }
      setOtp(newOtp);
      // Focus the last input
      const lastInput = document.getElementById(`admin-reset-otp-5`);
      if (lastInput) lastInput.focus();
    } else {
      // If less than 6 digits, fill from the start index
      for (let i = 0; i < digits.length && (startIndex + i) < 6; i++) {
        newOtp[startIndex + i] = digits[i];
      }
      setOtp(newOtp);
      // Focus the next empty input or the last one
      const focusIndex = Math.min(startIndex + digits.length, 5);
      const focusInput = document.getElementById(`admin-reset-otp-${focusIndex}`);
      if (focusInput) focusInput.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) return;
    if (passwordStrength < 75) return;

    setIsLoading(true);
    try {
      const code = otp.join("");
      await resetAdminPassword(email, code, password);
      navigate("/admin/login");
    } catch (error) {
      // Error is handled in the context
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-8 items-center">
        {/* Left Side - Branding */}
        <div className="hidden lg:block">
          <div className="relative">
            <img
              src="https://placehold.co/500x600"
              alt="Admin password reset"
              width={500}
              height={600}
              className="rounded-2xl"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-gray-800/50 to-transparent rounded-2xl"></div>
            <div className="absolute bottom-8 left-8 text-white">
              <h2 className="text-3xl font-bold mb-2">Reset Admin Password</h2>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <span className="text-2xl font-bold">Security Check</span>
              </div>
              <p className="text-white/90 max-w-sm">
                Enter your verification code and create a new secure password.
              </p>
            </div>
          </div>
        </div>

        {/* Right Side - Reset Form */}
        <div className="w-full max-w-md mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="lg:hidden flex items-center justify-center gap-2 mb-4">
                <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                  <Shield className="w-6 h-6 text-white" />
                </div>
              </div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">
                Reset Admin Password
              </h1>
              <p className="text-gray-600">Enter your email, OTP, and new password</p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="email" className="text-gray-800 font-medium">
                  Email Address
                </Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your admin email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 py-3 border-gray-200 focus:border-primary"
                    required
                  />
                </div>
              </div>

              <div>
                <Label className="text-gray-800 font-medium mb-4 block text-center">
                  Enter 6-Digit OTP
                </Label>
                <div className="flex gap-2 justify-center">
                  {otp.map((digit, index) => (
                    <Input
                      key={index}
                      id={`admin-reset-otp-${index}`}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      onPaste={(e) => handleOtpPaste(e, index)}
                      className="w-12 h-12 text-center text-lg font-semibold border-gray-200 focus:border-primary"
                      required
                    />
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-gray-800 font-medium">
                  New Password
                </Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Create new password"
                    className="pl-10 pr-10 py-3 border-gray-200 focus:border-primary"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>

                {/* Password Strength Indicator */}
                {password && (
                  <div className="mt-3">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${getStrengthColor(passwordStrength)}`}
                          style={{ width: `${passwordStrength}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-gray-600">{getStrengthText(passwordStrength)}</span>
                    </div>
                    <div className="text-xs text-gray-500 space-y-1">
                      <div className="flex items-center gap-2">
                        {password.length >= 8 ? (
                          <Check className="w-3 h-3 text-green-500" />
                        ) : (
                          <X className="w-3 h-3 text-gray-400" />
                        )}
                        <span>At least 8 characters</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {/[A-Z]/.test(password) ? (
                          <Check className="w-3 h-3 text-green-500" />
                        ) : (
                          <X className="w-3 h-3 text-gray-400" />
                        )}
                        <span>One uppercase letter</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {/[0-9]/.test(password) ? (
                          <Check className="w-3 h-3 text-green-500" />
                        ) : (
                          <X className="w-3 h-3 text-gray-400" />
                        )}
                        <span>One number</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="text-gray-800 font-medium">
                  Confirm New Password
                </Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm new password"
                    className="pl-10 pr-10 py-3 border-gray-200 focus:border-primary"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {confirmPassword && password !== confirmPassword && (
                  <p className="text-red-500 text-xs mt-2 flex items-center gap-1">
                    <X className="w-3 h-3" />
                    Passwords do not match
                  </p>
                )}
                {confirmPassword && password === confirmPassword && (
                  <p className="text-green-500 text-xs mt-2 flex items-center gap-1">
                    <Check className="w-3 h-3" />
                    Passwords match
                  </p>
                )}
              </div>

              <Button
                type="submit"
                disabled={isLoading || password !== confirmPassword || passwordStrength < 75 || otp.join("").length !== 6}
                className="w-full bg-primary hover:bg-primary/90 text-white py-3 text-lg font-medium"
              >
                {isLoading ? "Resetting Password..." : "Reset Password"}
              </Button>
            </form>

            {/* Back to Login */}
            <div className="text-center mt-6">
              <Link
                to="/admin/login"
                className="inline-flex items-center gap-2 text-gray-600 hover:text-primary font-medium transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Admin Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
